import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PDFUpload from "../../components/PDFUpload";
import RoleExtractor from "../../components/RoleExtractor";
import ExperienceInput from "../../components/ExperienceInput";
import QuestionGenerator from "../../components/QuestionGenerator";
import FeedbackEvaluator from "../../components/FeedbackEvaluator";

const ProgressBar = ({ step, total }) => {
  const progressPercent = (step / total) * 100;
  return (
    <div className="w-full px-2 mb-6">
      <div className="w-full bg-gray-300 rounded-full h-2 overflow-hidden">
        <motion.div
          className="bg-gradient-to-r from-purple-500 to-violet-600 h-2 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progressPercent}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <p className="text-sm text-gray-600 mt-2 text-center">
        Step {Math.min(step, 6)} of 6
      </p>
    </div>
  );
};

const containerVariants = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, y: -30, transition: { duration: 0.3 } },
};

export default function AIMockInterview() {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState("");
  const [experience, setExperience] = useState({});
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [listening, setListening] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [permissionError, setPermissionError] = useState(false);

  useEffect(() => {
    if (step === 6 && questions.length > 0) {
      speakQuestion(questions[currentQuestionIndex]);
    }
  }, [step, currentQuestionIndex, questions]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") {
        exitFullScreen();
        window.speechSynthesis?.cancel();
        setStep(7);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const enterFullScreen = async () => {
    const el = document.documentElement;
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else if (el.mozRequestFullScreen) await el.mozRequestFullScreen();
      else if (el.msRequestFullscreen) await el.msRequestFullscreen();
      setIsFullScreen(true);
    } catch (err) {
      console.warn("Fullscreen request failed", err);
      setPermissionError(true);
    }
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
    else if (document.msExitFullscreen) document.msExitFullscreen();
    setIsFullScreen(false);
  };

  const speakQuestion = (text) => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    synth.speak(utterance);
  };

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    setListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setCurrentAnswer(transcript);
      setListening(false);
    };

    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);

    recognition.start();
  };

  // ✅ FIXED PERMISSION HANDLING
  const requestPermissionsAndProceed = async () => {
    setPermissionError(false);
    try {
      // First: ask for audio/video
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });

      // Stop stream after permission granted
      stream.getTracks().forEach((track) => track.stop());

      // Then: request fullscreen
      await enterFullScreen();

      // All successful
      setStep(6);
    } catch (err) {
      console.error("Permission or fullscreen error:", err);
      setPermissionError(true);
    }
  };

  const submitSpeechAnswer = () => {
    if (!currentAnswer.trim()) {
      alert("Please speak your answer before submitting.");
      return;
    }

    setAnswers((prev) => [...prev, currentAnswer.trim()]);
    setCurrentAnswer("");

    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      speakQuestion(questions[nextIndex]);
    } else {
      exitFullScreen();
      setStep(7);
    }
  };

  const handlePDFUploaded = () => setStep(2);
  const handleRoleConfirmed = (extractedRole) => {
    setRole(extractedRole);
    setStep(3);
  };
  const handleExperienceChange = (expObj) => {
    setExperience(expObj);
    setStep(4);
  };
  const handleQuestionsGenerated = (generatedQuestions) => {
    setQuestions(generatedQuestions);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setStep(5);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-100 flex items-center justify-center py-10 px-4">
      <motion.div
        className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl p-6"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={containerVariants}
      >
        <h1 className="text-3xl font-bold text-center text-indigo-800 mb-6">
          🧠 AI Mock Interview Assistant
        </h1>

        <ProgressBar step={step} total={6} />

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" {...containerVariants}>
              <PDFUpload onUpload={handlePDFUploaded} />
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" {...containerVariants}>
              <RoleExtractor onConfirm={handleRoleConfirmed} />
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step3" {...containerVariants}>
              <ExperienceInput onExperienceChange={handleExperienceChange} />
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="step4" {...containerVariants}>
              <QuestionGenerator
                role={role}
                experience={experience.years || 0}
                setQuestions={handleQuestionsGenerated}
              />
            </motion.div>
          )}

          {step === 5 && (
            <motion.div key="step5" {...containerVariants}>
              <div className="text-center">
                <p className="text-xl text-gray-700 font-semibold mb-4">
                  🎤 Please allow access to your microphone and webcam to begin
                  the interview. We’ll also enter full-screen for immersion.
                </p>
                <button
                  onClick={requestPermissionsAndProceed}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700"
                >
                  Allow & Proceed
                </button>
                {permissionError && (
                  <p className="text-red-600 mt-4">
                    ❌ Mic or camera permission was denied or fullscreen failed.
                    Please check your browser settings and reload the page.
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {step === 6 && questions.length > 0 && (
            <motion.div key="step6" {...containerVariants}>
              <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center px-4">
                <h2 className="text-3xl font-bold text-indigo-700 mb-4 text-center">
                  🎤 Speak Your Answer
                </h2>
                <button
                  onClick={startListening}
                  disabled={listening}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-lg"
                >
                  {listening ? "Listening..." : "Start Recording"}
                </button>

                <div className="bg-gray-100 p-3 rounded-md text-gray-800 mt-6 w-full max-w-xl">
                  <strong>Your Answer:</strong>{" "}
                  {currentAnswer || <em>Not recorded yet</em>}
                </div>

                <div className="flex justify-between items-center w-full max-w-xl text-sm text-gray-500 mt-6">
                  <span>
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <button
                    onClick={submitSpeechAnswer}
                    className="text-indigo-600 underline"
                  >
                    Submit Answer
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {step === 7 && (
            <motion.div key="step7" {...containerVariants}>
              <FeedbackEvaluator questions={questions} answers={answers} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
